import { App, Button, Tooltip } from 'antd'
import { Check, Copy } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useStyles } from './styles'

const COPIED_FEEDBACK_MS = 1600

type Props = {
  text: string
  className?: string
  showTooltip?: boolean
}

export const CopyButton = ({ text, className, showTooltip = true }: Props) => {
  const { t } = useTranslation()
  const { styles, cx } = useStyles()
  const { message } = App.useApp()
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(timer.current), [])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS)
    } catch {
      message.error(t('workspaceHome.copy.failed'))
    }
  }

  const label = copied
    ? t('workspaceHome.copy.copied')
    : t('workspaceHome.copy.copy')

  const button = (
    <Button
      type="text"
      size="small"
      className={cx(
        styles.actionButton,
        copied && styles.actionButtonDone,
        className
      )}
      icon={copied ? <Check size={14} /> : <Copy size={14} />}
      aria-label={label}
      onClick={handleCopy}
    />
  )

  return showTooltip ? <Tooltip title={label}>{button}</Tooltip> : button
}
